import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

// material-ui
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports

// assets
import { IconEyeOff, IconLock, IconLogout, IconSettings, IconShieldLock } from "@tabler/icons";
import { Switch } from "@mui/material";
import { DEMO_ORGANIZATION_NAME, toggleDataPrivacy, useDataPrivacy } from "../../../../../../utils/dataPrivacy";
import { useDispatch, useSelector } from "react-redux";
import config from "../../../../../../../config";
import {
    ConfigPageIndex,
    ImageConfigs
} from "../../../../../../../domain/entities/Configs";
import { useAuth } from "../../../../../../hooks/useAuth";
import { loadConfigs } from "../../../../../../redux/actions/administrator";
import ImageLoader from "../../../../../pages/administrator/ImageLoader";
import Transitions from "../../../../widgets/extended/Transitions";
import ChangePasswordDialog from "./ChangePasswordDialog";
import MFASetupDialog from "./MFASetupDialog";

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const theme = useTheme();
  //const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sdm, setSdm] = useState(true);
  const [value, setValue] = useState("");
  const [notification, setNotification] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const { logout, user } = useAuth();
  // Data Privacy Mode (demos): Paisley admins only. While on, the district's name and logo are hidden too.
  const dataPrivacy = useDataPrivacy();
  const isPaisleyAdmin = (user?.role?.level ?? 0) >= 4;
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    logout();
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const siteConfigs = useSelector((state) => state.administrator.siteConfigs);

  const handleListItemClick = (event, index, route = "") => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== "") {
      navigate(route);
    }
  };
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  function stringToColor(string) {
    const colors = [
      "#1e88e5", "#5e35b1", "#00c853", "#ffc107", "#4527a0", "#1565c0", 
    ];
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  function stringAvatar(name) { 
    return {
      sx: {
        // Setting the background color using the stringToColor function
        bgcolor: stringToColor(name),
        // text color
        color: "#ffffff",  
      },
      // Constructing the children property with the initials of the name, filtering out empty strings
      children: `${name?.split(" ").filter(Boolean)[0][0]}${name?.split(" ").filter(Boolean)[1][0]}`,
    };
  }

  useEffect(() => {
    if (user?.organization?.id && user?.role?.level > 1) {
      if (!siteConfigs?.siteConfigs) {
        dispatch(
          loadConfigs(user.organization.id, ConfigPageIndex.CONFIGURATIONS)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box>
        {siteConfigs?.siteConfigs?.showBannerLogo === true && !dataPrivacy && (
          <ImageLoader
            fileName={ImageConfigs.SCHOOL_LOGO}
            alt={ImageConfigs.SCHOOL_LOGO}
            style={{
              height: "40px",
              objectFit: "contain",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          maxWidth: {
            xs: "200px",
            sm: "150px",
          },
          textAlign: "center",
          marginX: 2,
        }}
      >
        <Typography variant="h4" component="h1" color="white">
          {user ? (dataPrivacy ? DEMO_ORGANIZATION_NAME : user.organization.name) : ""}
        </Typography>
      </Box>
      {user && (
        <Chip
          sx={{
            height: "48px",
            alignItems: "center",
            borderRadius: "27px",
            transition: "all .2s ease-in-out",
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.primary.light,
            '&[aria-controls="menu-list-grow"], &:hover': {
              borderColor: theme.palette.primary.main,
              background: `${theme.palette.primary.main}!important`,
              color: theme.palette.primary.light,
              "& svg": {
                stroke: theme.palette.primary.light,
              },
            },
            "& .MuiChip-label": {
              lineHeight: 0,
            },
          }}
          icon={
            <Avatar
              /*src={User1}*/
              sx={{
                ...theme.typography.mediumAvatar,
                margin: "8px 0 8px 8px !important",
                cursor: "pointer",
              }}
              ref={anchorRef}
              aria-controls={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              color="inherit"
              {...stringAvatar(
                user ? `${user?.firstName} ${user?.lastName}` : "Demo User" 
              )}
            />
          }
          label={
            <IconSettings
              stroke={1.5}
              size="1.5rem"
              color={theme.palette.primary.main}
            />
          }
          variant="outlined"
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="primary"
        />
      )}
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper elevation={16} sx={{ boxShadow: theme.shadows[16] }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Hello,</Typography>
                        <Typography
                          component="span"
                          variant="h4"
                          sx={{ fontWeight: 400 }}
                        >
                          {user ? `${user.firstName} ${user.lastName}` : ""}
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle2">
                        {user?.role?.name}
                      </Typography>
                    </Stack>
                    {/*{process.env.REACT_APP_ENV === Environment.DEV && (
                      <OutlinedInput
                        sx={{ width: "100%", pr: 1, pl: 2, my: 2 }}
                        id="input-search-profile"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Search profile options"
                        startAdornment={
                          <InputAdornment position="start">
                            <IconSearch
                              stroke={1.5}
                              size="1rem"
                              color={theme.palette.grey[500]}
                            />
                          </InputAdornment>
                        }
                        aria-describedby="search-helper-text"
                        inputProps={{
                          "aria-label": "weight",
                        }}
                      />
                    )} */}
                  </Box>
                  {
                      <Box sx={{ p: 2 }}>
                        <Divider />
                        <List
                          component="nav"
                          sx={{
                            width: "100%",
                            maxWidth: 350,
                            minWidth: 300,
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: "10px",
                            [theme.breakpoints.down("md")]: {
                              minWidth: "100%",
                            },
                            "& .MuiListItemButton-root": {
                              mt: 0.5,
                            },
                          }}
                        >
                          {user?.role && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${config.borderRadius}px`,
                            }}
                            onClick={() => {
                              setOpen(false);
                              setChangePasswordOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <IconLock stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">Change Password</Typography>
                              }
                            />
                          </ListItemButton>
                          )}
                          {user?.role && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${config.borderRadius}px`,
                            }}
                            onClick={() => {
                              setOpen(false);
                              setMfaSetupOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <IconShieldLock stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">Two-Factor Auth (MFA)</Typography>
                              }
                            />
                          </ListItemButton>
                          )}
                          {isPaisleyAdmin && (
                          <ListItemButton
                            sx={{
                              borderRadius: `${config.borderRadius}px`,
                            }}
                            role="switch"
                            aria-checked={dataPrivacy}
                            onClick={() => {
                              setOpen(false);
                              toggleDataPrivacy();
                            }}
                          >
                            <ListItemIcon>
                              <IconEyeOff stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={<Typography variant="body2">Data Privacy Mode</Typography>}
                              secondary={<Typography variant="caption" color="text.secondary">Masks names and schools for demos</Typography>}
                            />
                            <Switch edge="end" size="small" checked={dataPrivacy} tabIndex={-1} inputProps={{ "aria-hidden": true }} />
                          </ListItemButton>
                          )}
                          <ListItemButton
                            sx={{
                              borderRadius: `${config.borderRadius}px`,
                            }}
                            selected={selectedIndex === 4}
                            onClick={handleLogout}
                          >
                            <ListItemIcon>
                              <IconLogout stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">Logout</Typography>
                              }
                            />
                          </ListItemButton>
                        </List>
                      </Box>
                  }
                  {/* {process.env.REACT_APP_ENV === Environment.DEV && (
                    <PerfectScrollbar
                      style={{
                        height: "auto",
                        maxHeight: "calc(100vh - 250px)",
                        overflowX: "hidden",
                        overflowY: "auto",
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <UpgradePlanCard />
                        <Divider />
                        <Card
                          sx={{
                            bgcolor: theme.palette.primary.light,
                            my: 2,
                          }}
                        >
                          <CardContent>
                            <Grid container spacing={3} direction="column">
                              <Grid item>
                                <Grid
                                  item
                                  container
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Grid item>
                                    <Typography variant="subtitle1">
                                      Start DND Mode
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Switch
                                      color="primary"
                                      checked={sdm}
                                      onChange={(e) => setSdm(e.target.checked)}
                                      name="sdm"
                                      size="small"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid
                                  item
                                  container
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Grid item>
                                    <Typography variant="subtitle1">
                                      Allow Notifications
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Switch
                                      checked={notification}
                                      onChange={(e) =>
                                        setNotification(e.target.checked)
                                      }
                                      name="sdm"
                                      size="small"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                        <Divider />
                        <List
                          component="nav"
                          sx={{
                            width: "100%",
                            maxWidth: 350,
                            minWidth: 300,
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: "10px",
                            [theme.breakpoints.down("md")]: {
                              minWidth: "100%",
                            },
                            "& .MuiListItemButton-root": {
                              mt: 0.5,
                            },
                          }}
                        >
                          <ListItemButton
                            sx={{ borderRadius: `${config.borderRadius}px` }}
                            selected={selectedIndex === 0}
                            onClick={(event) =>
                              handleListItemClick(event, 0, "#")
                            }
                          >
                            <ListItemIcon>
                              <IconSettings stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">
                                  Account Settings
                                </Typography>
                              }
                            />
                          </ListItemButton>
                          <ListItemButton
                            sx={{ borderRadius: `${config.borderRadius}px` }}
                            selected={selectedIndex === 1}
                            onClick={(event) =>
                              handleListItemClick(event, 1, "#")
                            }
                          >
                            <ListItemIcon>
                              <IconUser stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Grid
                                  container
                                  spacing={1}
                                  justifyContent="space-between"
                                >
                                  <Grid item>
                                    <Typography variant="body2">
                                      Social Profile
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Chip
                                      label="02"
                                      size="small"
                                      sx={{
                                        bgcolor: theme.palette.warning.dark,
                                        color: theme.palette.background.default,
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              }
                            />
                          </ListItemButton>

                          <ListItemButton
                            sx={{
                              borderRadius: `${config.borderRadius}px`,
                            }}
                            selected={selectedIndex === 4}
                            onClick={handleLogout}
                          >
                            <ListItemIcon>
                              <IconLogout stroke={1.5} size="1.3rem" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">Logout</Typography>
                              }
                            />
                          </ListItemButton>
                        </List>
                      </Box>
                    </PerfectScrollbar>
                  )} */}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <MFASetupDialog
        open={mfaSetupOpen}
        onClose={() => setMfaSetupOpen(false)}
      />
    </>
  );
};

export default ProfileSection;
